import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowSquareOut, Eye } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { SECTION_META } from "@/lib/meta";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function Markdown({ text }) {
  const lines = (text || "").split("\n");
  return (
    <div className="space-y-3 font-light text-sm leading-relaxed">
      {lines.map((line, i) => {
        if (line.startsWith("## ")) return <h3 key={i} className="font-head text-lg font-medium mt-4">{line.slice(3)}</h3>;
        if (line.startsWith("# ")) return <h2 key={i} className="font-head text-2xl font-light">{line.slice(2)}</h2>;
        if (line.trim() === "") return <div key={i} className="h-1" />;
        const bold = line.replace(/\*\*(.+?)\*\*/g, "<strong class='text-foreground font-semibold'>$1</strong>");
        return <p key={i} className="text-muted-foreground" dangerouslySetInnerHTML={{ __html: bold }} />;
      })}
    </div>
  );
}

export default function Services() {
  const { section } = useParams();
  const meta = SECTION_META[section] || SECTION_META["seo"];
  const Icon = meta.icon;
  const [viewing, setViewing] = useState(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["services", section],
    queryFn: async () => (await api.get(`/services?section=${section}`)).data,
  });

  return (
    <div className="space-y-8" data-testid={`services-${section}`}>
      <div className="flex items-center gap-4 border-b border-border pb-6">
        <div className="w-12 h-12 border border-border flex items-center justify-center bg-card">
          <Icon size={26} className="text-primary" />
        </div>
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-primary">Services</span>
          <h1 className="font-head text-3xl font-light tracking-tight">{meta.label}</h1>
        </div>
      </div>

      {isLoading ? (
        <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground animate-pulse">Loading…</p>
      ) : items.length === 0 ? (
        <div className="border border-border p-12 text-center" data-testid="services-empty">
          <p className="text-muted-foreground font-light">No {meta.label} content available for your access level yet.</p>
        </div>
      ) : (
        <div className="grid gap-px bg-border border border-border md:grid-cols-2">
          {items.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="group bg-card flex flex-col overflow-hidden"
              data-testid={`service-${s.id}`}
            >
              {s.image_url && (
                <div className="relative h-40 overflow-hidden border-b border-border">
                  <div className="absolute inset-0 bg-cover bg-center opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" style={{ backgroundImage: `url(${s.image_url})` }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
                </div>
              )}
              <div className="p-6 flex flex-col flex-1">
                {s.subtitle && <span className="font-mono text-[10px] tracking-widest uppercase text-primary mb-2">{s.subtitle}</span>}
                <h3 className="font-head text-xl font-medium leading-snug">{s.title}</h3>
                <p className="text-sm text-muted-foreground font-light mt-2 flex-1">{s.description}</p>
                <div className="mt-5 pt-4 border-t border-border flex items-center gap-3">
                  <button
                    onClick={() => setViewing(s)}
                    data-testid={`service-view-${s.id}`}
                    className="flex items-center gap-2 border border-border px-4 py-2 text-xs font-mono tracking-wide hover:border-primary hover:text-primary transition-colors"
                  >
                    <Eye size={16} /> Details
                  </button>
                  {s.cta_url && (
                    <a href={s.cta_url} target="_blank" rel="noreferrer" data-testid={`service-cta-${s.id}`} className="flex items-center gap-2 text-xs font-mono tracking-wide text-primary hover:underline">
                      {s.cta_label || "Learn more"} <ArrowSquareOut size={14} />
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="rounded-none border-border bg-card max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="service-viewer">
          <DialogHeader>
            {viewing?.subtitle && <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-primary text-left">{viewing.subtitle}</span>}
            <DialogTitle className="font-head text-2xl font-light text-left">{viewing?.title}</DialogTitle>
          </DialogHeader>
          <div className="mt-2"><Markdown text={viewing?.body || viewing?.description} /></div>
          {viewing?.cta_url && (
            <a href={viewing.cta_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 text-xs font-mono text-primary hover:underline">
              {viewing.cta_label || "Open"} <ArrowSquareOut size={14} />
            </a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
